// Data resolvers for route navigation
import store from '@/store';
import ProductService from '@/services/ProductService';
import SeasonsService from '@/services/SeasonsService';
import ToastService from '@/services/ToastService';
import DocumentTypeService from '@/services/DocumentTypeService';
import GroupStructuresService from '@/services/GroupStructuresService';
import StructuresService from '@/services/StructuresService';
import StorageTypesService from '@/services/StorageTypesService';
import i18n from '@/plugins/i18n';
import { Route } from 'vue-router';

const PROFILE = JSON.parse(localStorage.getItem('profile') || '{}');

/**
 * Interface for resolver functions
 * @returns Promise that resolves when data is loaded
 */
export type DataResolver = (to: Route, from: Route) => Promise<void>;

/**
 * Collection of data resolvers that can be used in route definitions
 */
export const resolvers = {
  /**
   * Loads product string filters into store
   */
  productStringFilter: async (to: Route, from: Route): Promise<void> => {
    try {
      const filters = {
        companyId: PROFILE.company_id,
        search: null,
        pageableDTO: {
          page: 0,
          size: 1000,
          sortBy: 'id',
          direction: 'DESC',
        }
      }
      
      const {data} = await ProductService.products(filters);
      store.commit('product/SET_PRODUCT_LIST', data);
      console.log('Products preloaded before navigation');
    } catch (error) {
      console.error('Error preloading products:', error);
      ToastService.error(i18n.t("product.notification.listError"), null, {
        icon: "mdi-alert-circle",
        queueable: true,
      });
    }
  },

  /**
   * Loads season string filters into store
   */
  seasonStringFilter: async (to: Route, from: Route): Promise<void> => {
    try {
      const filters = {
        companyId: PROFILE.company_id,
        search: null,
        pageableDTO: {
          page: 0,
          size: 1000,
          sortBy: 'id',
          direction: 'DESC',
        }
      }
      
      const {data} = await SeasonsService.seasons(filters);
      store.commit('season/SET_SEASON_LIST', data);
      console.log('Seasons preloaded before navigation');
    } catch (error) {
      console.error('Error preloading seasons:', error);
      ToastService.error(i18n.t("season.notification.listError"), null, {
        icon: "mdi-alert-circle",
        queueable: true,
      });
    }
  },

  /**
   * Loads document type filters into store
   */
  documentTypeFilter: async (to: Route, from: Route): Promise<void> => {
    try {
      const {data} = await DocumentTypeService.getDocumentTypesByCompany(PROFILE.company_id);
      store.commit('document_type/SET_DOCUMENT_TYPE_LIST', data);
      console.log('Document types preloaded before navigation');
    } catch (error) {
      console.error('Error preloading document types:', error);
      ToastService.error(i18n.t("documentType.notification.listError"), null, {
        icon: "mdi-alert-circle",
        queueable: true,
      });
    }
  },

  /**
   * Loads group structures default into store
   */
  groupStructuresDefault: async (to: Route, from: Route): Promise<void> => {
    try {
      const {data} = await GroupStructuresService.groupStructuresDefault();
      store.commit('groupDetails/SET_GROUP_STRUCTURE_DAFULTS', data);
      console.log('Group structures default preloaded before navigation');
    } catch (error) {
      console.error('Error preloading group structures default:', error);
      ToastService.error(i18n.t("groupStructure.notification.listError"), null, {
        icon: "mdi-alert-circle",
        queueable: true,
      });
    }
  },

  /**
   * Loads group structures by company into store
   */
  groupStructuresByCompany: async (to: Route, from: Route): Promise<void> => {
    try {
      const {data} = await GroupStructuresService.groupStructuresByCompany(PROFILE.company_id);
      store.commit('groupDetails/SET_GROUP_STRUCTURE_BY_COMPANY', data);
      console.log('Group structures by company preloaded before navigation');
    } catch (error) {
      console.error('Error preloading group structures by company:', error);
      ToastService.error(i18n.t("groupStructure.notification.listError"), null, {
        icon: "mdi-alert-circle",
        queueable: true,
      });
    }
  },

  /**
   * Loads structures list into store
   */
  structuresList: async (to: Route, from: Route): Promise<void> => {
    try {
      const {data} = await StructuresService.structuresByCompany(PROFILE.company_id);
      store.commit('records/SET_STRUCTURES_LIST', data);
      console.log('Structures list preloaded before navigation');
    } catch (error) {
      console.error('Error preloading structures list:', error);
      ToastService.error(i18n.t("records.notification.listError"), null, {
        icon: "mdi-alert-circle",
        queueable: true,
      });
    }
  },

  /**
   * Loads storage types by company into store
   */
  storageTypesByCompany: async (to: Route, from: Route): Promise<void> => {
    try {
      const {data} = await StorageTypesService.storageTypesByCompany(PROFILE.company_id);
      store.commit('storage_type/SET_STORAGE_TYPE_BY_COMPANY', data);
      console.log('Storage types by company preloaded before navigation');
    } catch (error) {
      console.error('Error preloading storage types by company:', error);
      ToastService.error(i18n.t("storageType.notification.listError"), null, {
        icon: "mdi-alert-circle",
        queueable: true,
      });
    }
  },

  /**
   * Combines multiple resolvers into one
   */
  combine: (...resolverFunctions: DataResolver[]): DataResolver => {
    return async (to: Route, from: Route): Promise<void> => {
      try {
        await Promise.all(resolverFunctions.map(resolver => resolver(to, from)));
      } catch (error) {
        console.error('Error in combined resolvers:', error);
        ToastService.error(i18n.t('common.error_loading') || 'Error cargando datos');
      }
    };
  }
};